import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; // Import useLocation
import axios from "axios";
import config from "./config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUsers,
  faFutbol,
  faBars,
  faChartLine,
  faSignOutAlt,
  faCreditCard,
  faEnvelope,
  faRobot,
  faQuestionCircle,
  faTimes, // Imported faTimes for the X icon
} from "@fortawesome/free-solid-svg-icons";
import "./css/Navigation.css";
const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};
const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

const Navigation = ({ firstName, signOut, hasUnfinishedMatch, userId }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMenuWarning, setShowMenuWarning] = useState(false);
  const [termsSigned, setTermsSigned] = useState(() => {
    const storedValue = localStorage.getItem("termsAccepted");
    return storedValue === "true"; // Initialize from localStorage
  });
  const [isDisabled, setIsDisabled] = useState(true); // Define the isDisabled state
  const [managerName, setManagerName] = useState(""); // Manager name state
  const [loading, setLoading] = useState(true);
  const location = useLocation(); // Hook to get the current route
  const navigate = useNavigate(); // Hook for programmatic navigation
  const toggleMenu = () => {
    if (hasUnfinishedMatch) {
      setShowMenuWarning(true);
      setTimeout(() => setShowMenuWarning(false), 3000); // Hide warning after 3 seconds
      return;
    }
    setIsMenuOpen(!isMenuOpen);
  };
  const [gamesLeft, setGamesLeft] = useState(0);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [freeTierUsed, setFreeTierUsed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const handleSignOut = () => {
    localStorage.removeItem("termsAccepted"); // Reset termsAccepted
    signOut(); // Call the original signOut function
  };
  useEffect(() => {
    const checkTermsSigned = async () => {
      const termsAccepted = localStorage.getItem("termsAccepted");

      if (termsAccepted === "true") {
        devLog("Terms already accepted, skipping API call.");
        setTermsSigned(true);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(API_URL, {
          operationType: "check_terms_and_policy_signed",
          data: { coach_id: userId },
        });

        devLog("Full API response:", response);

        // The Lambda now returns a simple boolean: true or false
        const isBothSigned = response.data;

        devLog("Lambda returned:", isBothSigned);

        if (isBothSigned) {
          devLog("Terms and privacy policy are signed.");
          setTermsSigned(true);
          localStorage.setItem("termsAccepted", "true");
        } else {
          devLog("Either terms or privacy policy are not signed.");
          setTermsSigned(false);
          localStorage.setItem("termsAccepted", "false");
        }
      } catch (error) {
        console.error("Error checking terms signed:", error);
        setTermsSigned(false);
        localStorage.setItem("termsAccepted", "false");
      } finally {
        setLoading(false);
      }
    };

    checkTermsSigned();
  }, [userId, location]);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axios.post(API_URL, {
          operationType: "check_coach_subscription",
          data: { coach_id: userId },
        });

        const data =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        // Directly access the correct values
        setCurrentPlan(data.subscription_type || "unknown"); // Adjusted to match backend response
        setFreeTierUsed(data.free_tier_used || false); // Boolean already converted
        setGamesLeft(data.games_left_this_month || 0); // No need for parseInt as backend sends number
        setSubscriptionStatus(data.subscription_status || "unknown");
      } catch (err) {
        console.error("Error fetching subscription details:", err);
        setError("Failed to fetch subscription details.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [userId]);

  useEffect(() => {
    devLog("useEffect triggered: Attempting to fetch manager name.");
    if (userId) {
      devLog(`User ID passed as prop: ${userId}`);
      fetchManagerName(userId);
    } else {
      console.warn("No User ID provided to Navigation.");
    }
  }, [userId]);

  const fetchManagerName = async (userId) => {
    devLog(`fetchManagerName called with userId: ${userId}`);
    try {
      const response = await fetch(`${API_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operationType: "fetch_coach_name",
          data: {
            coach_id: userId,
          },
        }),
      });

      devLog("API request sent. Awaiting response...");
      if (response.ok) {
        const responseData = await response.json();
        devLog("API response received:", responseData);

        // Parse the response body if it is still a string
        const data =
          typeof responseData.body === "string"
            ? JSON.parse(responseData.body)
            : responseData.body;

        devLog("Parsed data:", data);

        setManagerName(data.coach_name || "Manager"); // Update state with manager name
      } else {
        console.error(
          `Failed to fetch manager name. Status: ${response.status}`
        );
        setManagerName("Manager"); // Fallback name
      }
    } catch (error) {
      console.error("Error fetching manager name:", error);
      setManagerName("Manager"); // Fallback name
    }
  };

  if (!termsSigned) {
    return null;
  }
  return (
    <div className="navigation-container">
      {/* Small bubble hamburger/X icon */}
      <button className="menu-bubble" onClick={toggleMenu}>
        <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />{" "}
        {/* Toggle icon */}
      </button>

      {/* Warning Bubble */}
      {showMenuWarning && (
        <div className="warning-bubble">
          Match ongoing! Please finish the match first.
        </div>
      )}

      {/* Pop-up menu (wider when it appears) */}
      {isMenuOpen && (
        <nav className="popup-menu">
          <ul>
            <li className="menu-header">Hi, {managerName}</li>
            <li>
              <Link to="/" onClick={toggleMenu}>
                <FontAwesomeIcon icon={faHome} className="menu-icon" />
                Home
              </Link>
            </li>
            {subscriptionStatus !== "canceled" && (
              <>
                <li>
                  <Link to="/stats" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faChartLine} className="menu-icon" />
                    Team Stats
                  </Link>
                </li>
                <li>
                  <Link to="/manage-team" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faUsers} className="menu-icon" />
                    Manage Your Team
                  </Link>
                </li>
                <li>
                  <Link to="/games-stats" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faFutbol} className="menu-icon" />
                    Games Management
                  </Link>
                </li>
                <li>
                  <Link to="/how-to-use" onClick={toggleMenu}>
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="menu-icon"
                    />
                    How to Use?
                  </Link>
                </li>
                {/* <li>
                  <Link to="/coach-assistant" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faRobot} className="menu-icon" />{" "}
                    Coach Assistant
                  </Link>
                </li> */}
                <li>
                  <Link to="/contact-us" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faEnvelope} className="menu-icon" />
                    Contact Us
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link to="/manage-subscription" onClick={toggleMenu}>
                <FontAwesomeIcon icon={faCreditCard} className="menu-icon" />
                Manage Subscription
              </Link>
            </li>
            <li>
              <button onClick={handleSignOut} className="sign-out">
                <FontAwesomeIcon icon={faSignOutAlt} className="menu-icon" />
                Sign Out
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default Navigation;
